import type {
  BreakoutRoom,
  BreakoutRoomParticipant,
  ModerationParticipantPatch,
} from "@/zustand/CallModerationState";

/**
 * Normalizes a raw participant payload (from `getParticipantsInfo()` or a `participantJoined`/
 * `videoConferenceJoined` event) into the store's patch shape. Deliberately omits `isModerator` —
 * role changes are only ever applied via the dedicated `setParticipantRole` action so a re-fired
 * join event can never clobber a role learned from a separate `participantRoleChanged` event.
 *
 * Note: there is intentionally no guest/affiliation field here. The Jitsi iframe API exposes no
 * guest marker anywhere (verified against stable/jitsi-meet_11031 — see the TODO in
 * `ModerationParticipantRow.tsx` and ow2-chat-be#218).
 */
export function toModerationParticipant(
  raw: {
    participantId: string;
    displayName?: string;
    email?: string;
  },
  opts: { isLocal: boolean },
): ModerationParticipantPatch {
  return {
    id: raw.participantId,
    displayName: raw.displayName?.trim() || raw.participantId,
    isLocal: opts.isLocal,
  };
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Normalizes the Jitsi iframe's `breakoutRoomsUpdated` payload into the store's `BreakoutRoom[]`.
 *
 * Payload shape VERIFIED against stable/jitsi-meet_11031 (the deployed prosody/web tag):
 * prosody `mod_muc_breakout_rooms.lua` `broadcast_breakout_rooms` builds a map keyed by room NODE
 * (main room = its name; breakout rooms = a random uuid), each value
 * `{ id: <node == map key>, jid: <full room JID>, name, isMainRoom (main only), participants }`,
 * with `participants` keyed by occupant nick JID `<node>@<muc-domain>/<endpointId>` and values
 * `{ jid, role, displayName }`. jitsi-meet forwards it verbatim
 * (react/features/breakout-rooms/middleware.ts → API.js `notifyBreakoutRoomsUpdated`), and
 * external_api.js emits the transport message minus `name` — so the listener receives
 * `{ rooms: <map> }`. A bare map is still accepted defensively, and every field is treated as
 * possibly absent.
 *
 * Each room's `id` is its MAP KEY — the value the `sendParticipantToRoom` / `closeBreakoutRoom`
 * redux handlers resolve via `rooms[roomId]`. `jid` is kept for `removeBreakoutRoom`, which
 * resolves via `getRoomByJid` instead. Participant ids are the endpoint id derived from the
 * occupant-JID key (the part after "/"), mirroring jitsi's own derivation
 * (middleware.ts `participantJid.split('/')`), so they line up with the roster ids.
 */
export function toBreakoutRooms(payload: unknown): BreakoutRoom[] {
  // Unwrap the `{ rooms: <map> }` form; otherwise treat the payload itself as the map.
  const map =
    isPlainObject(payload) && "rooms" in payload ? payload.rooms : payload;
  if (!isPlainObject(map)) {
    return [];
  }

  const rooms: BreakoutRoom[] = [];
  for (const [roomKey, rawRoom] of Object.entries(map)) {
    if (!isPlainObject(rawRoom)) {
      continue;
    }
    const name = typeof rawRoom.name === "string" ? rawRoom.name.trim() : "";
    const jid = typeof rawRoom.jid === "string" ? rawRoom.jid : "";
    const participants: BreakoutRoomParticipant[] = [];
    if (isPlainObject(rawRoom.participants)) {
      for (const [pKey, rawParticipant] of Object.entries(
        rawRoom.participants,
      )) {
        if (!isPlainObject(rawParticipant)) {
          continue;
        }
        // Endpoint id from the occupant nick JID ("<room>@<muc>/<endpointId>"); when the key
        // carries no resource part (never observed in the verified payload) fall back to the
        // opaque map key itself. Deliberately NOT the participant's `jid` value — that is the
        // user's real connection JID, and since `displayName` falls back to the id below, using
        // it would surface a raw user JID as an on-screen name (dual-review finding).
        const slash = pKey.indexOf("/");
        const id = slash !== -1 ? pKey.slice(slash + 1) : pKey;
        // The FULL occupant nick JID = the map KEY itself. This is the id `sendParticipantToRoom`
        // must be given for a breakout occupant (see BreakoutRoomParticipant.jid). Kept only when
        // it is actually a JID (contains "@"); "" otherwise so a command is never fed a non-JID
        // that would silently no-op via the main-conference lookup path.
        const occupantJid = pKey.includes("@") ? pKey : "";
        const displayName =
          typeof rawParticipant.displayName === "string"
            ? rawParticipant.displayName.trim() || id
            : id;
        participants.push({ id, jid: occupantJid, displayName });
      }
    }
    rooms.push({
      id: roomKey,
      jid,
      name,
      isMainRoom: rawRoom.isMainRoom === true,
      participants,
    });
  }
  return rooms;
}
